import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LocalStorageKey } from '@app/core/enums/local-storage-key.enum';
import { ContactsService } from '@app/modules/portfolio/services/contacts.service';
import { UserProfileService } from '@app/modules/user-profile/services/user-profile.service';
import { IUser } from '@common/models/IUser';
import { Utils } from '@common/utils/utils';

@Component({
  selector: 'app-user-chip',
  templateUrl: './user-chip.component.html',
  styleUrls: ['./user-chip.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UserChipComponent implements OnInit {

  @Input() userEmail: string | string[];
  @Input() isGuest: boolean = false;
  @Input() isTooltip: boolean = false;

  imagePath: SafeResourceUrl;
  toReturnImage: string;
  userName: string = '';
  @Input() compact: boolean = false;
  isHovered = false;
  defaultImage = 'assets/images/users/placeholder.jpg';

  constructor(
    private readonly userService: UserProfileService,
    private readonly sanitizer: DomSanitizer,
    private readonly contactsService: ContactsService
  ) {}

  ngOnInit(): void {
    if (!this.compact) {
      this.imagePath = 'assets/images/users/placeholder.jpg';
    } else {
      this.imagePath = null;
    }

    const email = this.normalizeEmail(this.userEmail);

    if (!email) {
      return;
    }

    this.userName = email;
    this.getUser(email);
  }
  get displayImage(): SafeResourceUrl | string {
    // If we have an actual image, always use it
    if (this.imagePath) return this.imagePath;

    // Compact mode + hover → show default image
    if (this.compact && this.isHovered) return this.defaultImage;

    // Otherwise null (so initials show)
    return null;
  }

  /**
   * Normalize input to a single email string
   */
  private normalizeEmail(value: any): string | null {
    if (typeof value === 'string') {
      return value;
    }

    if (Array.isArray(value) && typeof value[0] === 'string') {
      return value[0];
    }

    return null;
  }

  /**
   * Fetch user details
   */
  private getUser(email: string): void {
    this.userService.getUserByEmail(email).subscribe((res: IUser) => {
      if (res) {
        this.userName = res.name;
        this.toReturnImage = res.photo;

        if (this.toReturnImage) {
          this.imagePath = this.sanitizer.bypassSecurityTrustResourceUrl(
            'data:image/jpg;base64,' + this.toReturnImage
          );
        }
        else{
          // Only remove image in compact mode
          if (this.compact) {
            this.imagePath = null;
          } else {
            this.imagePath = 'assets/images/users/placeholder.jpg';
          }
        }
      } else if (this.isGuest) {
        this.resolveGuestUser(email);
      }
    });
  }

  /**
   * Resolve guest user from localStorage
   */
  private resolveGuestUser(email: string): void {
    let displayName = null;
    // Set display name for external user
    const externalUserContacts = this.contactsService.getExternalContactsCache();
    if (externalUserContacts.length) {
      // External user - look up in contacts list
      const contact = externalUserContacts.find(c => c.email?.toLowerCase() === email || c?._id === email);
      if (contact) {
        if (contact?.name) {
          displayName = contact.name;
        } else if (contact?.firstName && contact?.lastName) {
          displayName = `${contact.firstName} ${contact.lastName}`;
        } else if (contact?.firstName) {
          displayName = contact.firstName;
        } else if (contact?.lastName) {
          displayName = contact.lastName;
        }
      }
    }

    const guestUsersStr = localStorage.getItem(LocalStorageKey.guestUserCache);
    if (!displayName && Utils.isValidEmail(email) && guestUsersStr) {
      const guestUsers = JSON.parse(guestUsersStr);
      const user = guestUsers.find(
        (u: any) =>
          typeof u.mail === 'string' &&
          u.mail.toLowerCase() === email.toLowerCase()
      );

      if (user) {
        displayName = user.displayName || email;
      }
    }

    this.userName = displayName || email;
  }
  onMouseEnter() {
    this.isHovered = true;
  }

  onMouseLeave() {
    this.isHovered = false;
  }
  get showLabel(): boolean {
    if (!this.compact) return true;
    return this.isHovered;
  }

  setStyleClass(): string {
    let className = '';
    if (this.isTooltip) {
      className = 'compact-chip compact-chip-text';
    } else if (this.compact) {
      className = 'compact-chip compact-chip-text expand-chip';
    } else {
      className = 'mr-2';
    }

    if ((this.isTooltip || this.compact) && !this.displayImage) {
      className += ' content-text';
    }
    return className;
  }
}
